<template>
    <section
        class="permission-help"
        :class="{ 'permission-help--compact': compact, 'permission-help--notice': reason !== 'blocked' }"
        data-test="qr-permission-help"
    >
        <div class="permission-help__intro" role="status" aria-live="polite">
            <div>
                <h2>{{ $t(`qr.permission.title.${reason}`, { capability: $t(`qr.permission.capability.${capability}`) }) }}</h2>
                <p v-if="!compact">{{ $t(`qr.permission.purpose.${capability}`) }}</p>
                <p v-if="reason !== 'blocked'">{{ $t(`qr.permission.reason.${reason}`) }}</p>
            </div>
        </div>
        <button
            v-if="reason === 'blocked'"
            ref="helpButton"
            type="button"
            class="permission-help__enable"
            aria-haspopup="dialog"
            data-test="qr-permission-enable"
            @click="openHelp"
        >
            {{ $t('qr.permission.help') }}
        </button>
        <Teleport to="body">
            <dialog
                ref="helpDialog"
                class="permission-help__dialog"
                :aria-label="$t('qr.permission.help')"
                data-test="qr-permission-dialog"
                @cancel.prevent="closeHelp"
                @close="restorePage"
            >
                <header class="permission-help__header">
                    <div class="permission-help__heading">
                        <div>
                            <h2>{{ $t('qr.permission.help') }}</h2>
                            <p>{{ $t(`qr.permission.capability.${capability}`) }}</p>
                        </div>
                    </div>
                    <button
                        ref="closeButton"
                        type="button"
                        class="permission-help__close"
                        :aria-label="$t('qr.permission.close')"
                        data-test="qr-permission-close"
                        @click="closeHelp"
                    >
                        <Icon name="close" class="permission-help__icon" aria-hidden="true" />
                    </button>
                </header>
                <div class="permission-help__steps">
                    <p>{{ $t('qr.permission.source_unknown') }}</p>
                    <ol>
                        <li>
                            <strong>{{ $t('qr.permission.site_heading') }}</strong>
                            <p>{{ $t(`qr.permission.site.${guidance.site}`, helpParams($t)) }}</p>
                        </li>
                        <li>
                            <strong>{{ $t('qr.permission.browser_heading') }}</strong>
                            <p>{{ $t(`qr.permission.browser_settings.${guidance.browserSettings}`, helpParams($t)) }}</p>
                        </li>
                        <li>
                            <strong>{{ $t('qr.permission.device_heading') }}</strong>
                            <p>{{ $t(`qr.permission.device.${guidance.device}`, helpParams($t)) }}</p>
                        </li>
                    </ol>
                    <p>{{ $t('qr.permission.return_to_retry') }}</p>
                    <p class="permission-help__note">{{ $t('qr.permission.labels_vary') }}</p>
                    <div class="permission-help__links">
                        <a v-if="guidance.siteUrl" :href="guidance.siteUrl" target="_blank" rel="noopener noreferrer">
                            {{ $t('qr.permission.official_browser_help') }}
                        </a>
                        <a v-if="guidance.deviceUrl" :href="guidance.deviceUrl" target="_blank" rel="noopener noreferrer">
                            {{ $t('qr.permission.official_device_help') }}
                        </a>
                    </div>
                    <p>{{ $t('qr.permission.still_blocked') }}</p>
                    <button type="button" class="btn btn-primary permission-help__done" @click="closeHelp">
                        {{ $t('qr.permission.back') }}
                    </button>
                </div>
            </dialog>
        </Teleport>
    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import Icon from '@/components/ui/Icon';
import { permissionGuidance } from '../../composables/qr/qrPermissions';

const props = defineProps({
    capability: { type: String, required: true },
    reason: { type: String, default: 'blocked' },
    compact: { type: Boolean, default: false },
});

const helpButton = ref(null);
const helpDialog = ref(null);
const closeButton = ref(null);
let previousOverflow;

function openHelp() {
    helpDialog.value.showModal();
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.value.focus();
}

function restorePage() {
    if (previousOverflow === undefined) return;
    document.body.style.overflow = previousOverflow;
    previousOverflow = undefined;
    helpButton.value?.focus();
}

function closeHelp() {
    helpDialog.value?.close();
    restorePage();
}

watch([() => props.capability, () => props.reason], closeHelp);
onBeforeUnmount(restorePage);

const guidance = computed(() => permissionGuidance(props.capability));
const helpParams = t => ({
    capability: t(`qr.permission.capability.${props.capability}`),
    browser: t(`qr.permission.browser.${guidance.value.browser}`),
});
</script>

<style scoped>
.permission-help {
    width: 100%;
    border: 1px solid #e7e2ed;
    border-radius: 12px;
    background: #f8f6fb;
    color: #222;
    text-align: center;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
}

.permission-help__intro {
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 14px 16px 0;
}

.permission-help__icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    color: #8662bf;
}

.permission-help h2 {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 700;
}

.permission-help p {
    margin: 6px 0 0;
    font-size: 14px;
    line-height: 1.5;
    color: #696969;
    overflow-wrap: anywhere;
}

.permission-help__enable {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
    padding: 4px 16px 12px;
    border: 0;
    border-radius: 0 0 12px 12px;
    line-height: 20px;
    background: transparent;
    color: #67419e;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.permission-help__enable:focus-visible,
.permission-help__dialog button:focus-visible,
.permission-help__dialog a:focus-visible {
    outline: 2px solid #4b2883;
    outline-offset: 2px;
}

.permission-help--compact h2 {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
}

.permission-help--compact .permission-help__intro {
    align-items: center;
    gap: 8px;
    padding: 12px 16px 0;
}

.permission-help--notice .permission-help__intro {
    padding-bottom: 14px;
}

.permission-help__dialog {
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    width: 100%;
    max-width: none;
    height: 100%;
    height: 100dvh;
    max-height: none;
    margin: 0;
    padding: 0;
    border: 0;
    background: #f2f2f2;
    color: #222;
    text-align: left;
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    overflow-y: auto;
}

.permission-help__dialog::backdrop {
    background: #f2f2f2;
}

.permission-help__header {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: calc(12px + env(safe-area-inset-top)) 20px 12px;
    background: #fff;
    border-bottom: 1px solid #e7e2ed;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.permission-help__heading {
    display: flex;
    align-items: center;
    gap: 16px;
}

.permission-help__dialog h2 {
    margin: 0;
    font-size: 20px;
    line-height: 26px;
    font-weight: 700;
}

.permission-help__dialog .permission-help__heading p {
    margin-top: 0;
    line-height: 20px;
}

.permission-help__dialog p {
    margin: 6px 0 0;
    color: #696969;
    font-size: 14px;
    line-height: 22px;
    overflow-wrap: anywhere;
}

.permission-help__close {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: #f3f0f7;
}

.permission-help__close .permission-help__icon {
    color: #222;
}

.permission-help__steps {
    max-width: 560px;
    margin: 0 auto;
    padding: 24px 20px calc(24px + env(safe-area-inset-bottom));
}

.permission-help__steps ol {
    margin: 16px 0;
    padding: 0;
    list-style: none;
    counter-reset: steps;
}

.permission-help__steps li {
    position: relative;
    padding: 20px 20px 20px 58px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    counter-increment: steps;
}

.permission-help__steps li::before {
    content: counter(steps);
    position: absolute;
    top: 18px;
    left: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f3f0f7;
    color: #67419e;
    font-weight: 700;
}

.permission-help__steps li + li {
    margin-top: 14px;
}

.permission-help__note {
    color: #696969;
}

.permission-help__links {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
}

.permission-help__dialog a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: #67419e;
    text-decoration: underline;
    font-size: 14px;
}

.permission-help__done {
    width: 100%;
    margin-top: 24px;
}
</style>
